import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import queueSocketClient from '../services/queueSocketClient';
import ChatStaffPanel from '../components/ChatStaffPanel';

// =====================================================
// Helpers
// =====================================================

/** Decode quoted-printable encoded text (=XX sequences and soft line breaks) */
const decodeQuotedPrintable = (text) => {
  if (!text) return '';
  return text
    .replace(/=\r?\n/g, '')  // remove soft line breaks
    .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
};

/** Strip HTML tags and decode common entities for plain-text display */
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Get displayable email body text — prefers body_text, falls back to stripped body_html */
const getEmailBodyText = (msg) => {
  let text = '';
  if (msg.body_text && msg.body_text.length > 20) text = msg.body_text;
  else if (msg.body_html) text = stripHtml(msg.body_html);
  else text = msg.body_text || '(no content)';
  // Decode any quoted-printable artifacts from email replies
  if (text.includes('=') && /=[0-9A-Fa-f]{2}/.test(text)) {
    text = decodeQuotedPrintable(text);
  }
  return text;
};

// =====================================================
// Constants
// =====================================================

const ESCALATION_LABELS = {
  LOW_CONFIDENCE: 'Low Confidence',
  UNCLEAR_INTENT: 'Unclear Intent',
  CLINICAL_QUESTION: 'Clinical Question',
  HUMAN_REQUESTED: 'Human Requested',
  CANCEL_REQUEST: 'Cancel Request',
  COMPLAINT: 'Patient Complaint',
  NO_BOOKING_LINK: 'No Booking Link',
  AMBIGUOUS_INTENT: 'Ambiguous Intent',
  REFILL_REQUEST: 'Refill Request',
  RESULTS_REQUEST: 'Results Request',
  BILLING_QUESTION: 'Billing Question',
  QUESTION_NEEDS_HUMAN: 'Question Needs a Person',
  UNKNOWN_SENDER: 'Unknown Sender',
  EMAIL_REPLY: 'Email Reply',
  AI_DETECTION_FAILED: 'AI Detection Failed',
  EMPTY_EMAIL_BODY: 'Empty Email',
  VERIFIED_PATIENT: 'Verified Patient',
  PATIENT_NOT_FOUND: 'Patient Not Found',
  UNVERIFIED_SENDER: 'Unverified Sender'
};

const URGENCY_COLORS = {
  high: { bg: '#fee2e2', text: '#991b1b', label: 'High' },
  medium: { bg: '#fef3c7', text: '#92400e', label: 'Medium' },
  low: { bg: '#f3f4f6', text: '#6b7280', label: 'Low' }
};

const SOURCE_CONFIG = {
  voice_ai: { label: 'Voice', bg: '#e0e7ff', text: '#4f46e5' },
  sms: { label: 'SMS', bg: '#dbeafe', text: '#1e40af' },
  chat_widget: { label: 'Chat', bg: '#d1fae5', text: '#065f46' },
  manual: { label: 'Manual', bg: '#f3f4f6', text: '#6b7280' },
  email: { label: 'Email', bg: '#fef3c7', text: '#92400e' }
};

// =====================================================
// Conversations Tab (existing SMS/Chat queue)
// =====================================================

function ConversationsTab({ practiceId, currentUserId }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, in_progress: 0, resolved_today: 0 });
  const [filter, setFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openChatSessionId, setOpenChatSessionId] = useState(null);
  const [openEmailId, setOpenEmailId] = useState(null);
  const [emailThread, setEmailThread] = useState([]);
  const [emailReplyText, setEmailReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [draftingReply, setDraftingReply] = useState(false);
  const [success, setSuccess] = useState(null);
  const [confirmReassign, setConfirmReassign] = useState(null); // item id being confirmed
  const socketConnected = useRef(false);

  const loadQueue = useCallback(async () => {
    if (!practiceId) return;
    try {
      const result = await api.getAiQueue(practiceId, { status: filter });
      if (result.success) {
        setQueue(result.data || []);
        setSummary(result.summary || { pending: 0, in_progress: 0, resolved_today: 0 });
      }
    } catch (err) {
      setError('Failed to load queue');
      console.error('Load AI queue error:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filter]);

  // WebSocket setup
  useEffect(() => {
    if (!practiceId) return;

    queueSocketClient
      .on('queueItemClaimed', (data) => {
        setQueue(prev => prev.map(item =>
          item.id === data.id
            ? { ...item, status: data.status || 'in_progress', assigned_to: data.assignedTo?.user_id, assigned_to_info: data.assignedTo }
            : item
        ));
      })
      .on('queueItemResolved', (data) => {
        setQueue(prev => prev.map(item =>
          item.id === data.id ? { ...item, status: 'resolved' } : item
        ));
      })
      .on('queueItemReleased', (data) => {
        setQueue(prev => prev.map(item =>
          item.id === data.id
            ? { ...item, status: 'pending', assigned_to: null, assigned_to_info: null, auto_released: true, previous_assigned_name: data.previousAssignedName }
            : item
        ));
      })
      .on('queueItemCreated', () => {
        // New item arrived — reload queue to get full item data
        loadQueue();
      })
      .on('queueSummaryUpdate', (data) => {
        setSummary(data);
      })
      .on('connect', () => {
        socketConnected.current = true;
      })
      .on('disconnect', () => {
        socketConnected.current = false;
      })
      .connect(practiceId);

    return () => {
      queueSocketClient.disconnect();
      socketConnected.current = false;
    };
  }, [practiceId, loadQueue]);

  // Polling (30s fallback — WebSocket is primary)
  useEffect(() => {
    loadQueue();
    const interval = setInterval(loadQueue, 30000);
    return () => clearInterval(interval);
  }, [loadQueue]);

  const showToast = (msg, type = 'success') => {
    if (type === 'error') {
      setError(msg);
      setTimeout(() => setError(null), 4000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleTakeover = async (item) => {
    if (item.source === 'email') {
      if (openEmailId === item.id) {
        setOpenEmailId(null);
        setEmailThread([]);
      } else {
        setOpenEmailId(item.id);
        try {
          const result = await api.getEmailThread(item.id, practiceId);
          setEmailThread(result.data || []);
        } catch (err) {
          console.error('Load thread error:', err);
          setEmailThread([]);
        }
      }
      return;
    }
    if (item.source === 'chat_widget' && item.chat_session_id) {
      setOpenChatSessionId(item.chat_session_id);
      return;
    }
    try {
      await api.aiTakeover(practiceId, item.sub_conversation_id);
      navigate(`/practice-view/messaging?conversation=${item.conversation_id}`);
    } catch (err) {
      if (err.message?.includes('already claimed') || err.message?.includes('409')) {
        showToast('This item was just claimed by another team member. You can reassign it to yourself if needed.', 'error');
        loadQueue();
      } else {
        showToast(err.message || 'Takeover failed', 'error');
      }
    }
  };

  const handleReassign = async (item) => {
    try {
      await api.reassignQueueItem(item.id);
      setConfirmReassign(null);
      showToast('Item reassigned to you');
      loadQueue();
    } catch (err) {
      showToast(err.message || 'Reassign failed', 'error');
    }
  };

  const handleUnassign = async (item) => {
    try {
      await api.unassignQueueItem(item.id);
      showToast('Item released back to pending');
      loadQueue();
    } catch (err) {
      showToast(err.message || 'Release failed', 'error');
    }
  };

  const handleResolveEmail = async (item) => {
    try {
      await api.resolveAiQueueItem(item.id);
      loadQueue();
      setOpenEmailId(null);
    } catch (err) {
      if (err.message?.includes('assigned to another')) {
        showToast('This item is assigned to another team member. Reassign to yourself first.', 'error');
      } else {
        showToast(err.message || 'Resolve failed', 'error');
      }
    }
  };

  const handleDraftReply = async (item) => {
    setDraftingReply(true);
    try {
      const result = await api.draftEmailReply(item.id, practiceId);
      if (result.success && result.draft) {
        setEmailReplyText(result.draft);
      } else {
        showToast('Failed to generate draft', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Failed to generate AI draft', 'error');
    } finally {
      setDraftingReply(false);
    }
  };

  const handleSendEmailReply = async (item) => {
    if (!emailReplyText.trim()) return;
    setSendingReply(true);
    try {
      await api.replyToEmail(item.id, emailReplyText.trim(), undefined, practiceId);
      setEmailReplyText('');
      setOpenEmailId(null);
      showToast('Reply sent successfully');
      loadQueue();
    } catch (err) {
      if (err.message?.includes('assigned to another')) {
        showToast('This item is assigned to another team member. Reassign to yourself first.', 'error');
      } else if (err.message?.includes('already resolved')) {
        showToast('This item was already resolved by another team member.', 'error');
        loadQueue();
      } else {
        showToast(err.message || 'Failed to send reply', 'error');
      }
    } finally {
      setSendingReply(false);
    }
  };

  const filteredQueue = sourceFilter === 'all'
    ? queue
    : sourceFilter === 'complaints'
      ? queue.filter(item => item.escalation_reason === 'COMPLAINT')   // commons #273
      : queue.filter(item => (item.source || 'sms') === sourceFilter);

  const isOwnItem = (item) => item.assigned_to === currentUserId;
  const isOtherUserItem = (item) => item.assigned_to && item.assigned_to !== currentUserId;

  if (loading) return <div style={styles.loading}>Loading conversations...</div>;

  return (
    <div>
      {error && <div style={styles.errorBanner}>{error}</div>}
      {success && <div style={{ ...styles.errorBanner, background: '#d1fae5', color: '#065f46', borderColor: '#6ee7b7' }}>{success}</div>}

      <div style={styles.summaryRow}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f59e0b' }}>
          <div style={styles.summaryValue}>{summary.pending}</div>
          <div style={styles.summaryLabel}>Pending</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3b82f6' }}>
          <div style={styles.summaryValue}>{summary.in_progress}</div>
          <div style={styles.summaryLabel}>In Progress</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #10b981' }}>
          <div style={styles.summaryValue}>{summary.resolved_today}</div>
          <div style={styles.summaryLabel}>Resolved Today</div>
        </div>
      </div>

      <div style={styles.filterRow}>
        {['pending', 'in_progress', 'resolved', 'all'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              ...styles.filterButton,
              ...(filter === status ? styles.filterButtonActive : {})
            }}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
        <span style={{ borderLeft: '1px solid #e5e7eb', margin: '0 4px' }} />
        {['all', 'sms', 'chat_widget', 'email', 'complaints'].map(src => (
          <button
            key={src}
            onClick={() => setSourceFilter(src)}
            style={{
              ...styles.filterButton,
              ...(sourceFilter === src ? styles.filterButtonActive : {})
            }}
          >
            {src === 'all' ? 'All Sources' : src === 'sms' ? 'SMS' : src === 'email' ? 'Email' : src === 'complaints' ? '⚠ Complaints' : 'Chat'}
          </button>
        ))}
      </div>

      <div style={styles.queueList}>
        {filteredQueue.length === 0 ? (
          <div style={styles.emptyState}>
            {filter === 'pending'
              ? 'No conversations need attention right now.'
              : `No ${filter.replace('_', ' ')} items.`}
          </div>
        ) : (
          filteredQueue.map(item => {
            const assignedToOther = isOtherUserItem(item);
            const assignedToMe = isOwnItem(item);
            const assigneeName = item.assigned_to_info?.name || '';

            return (
            <div key={item.id}>
              <div style={{
                ...styles.queueItem,
                ...(assignedToMe ? { borderLeft: '3px solid #3b82f6' } : {}),
                ...(assignedToOther ? { opacity: 0.85 } : {})
              }}>
                <div style={styles.queueItemLeft}>
                  <div style={styles.patientRow}>
                    <div style={styles.avatar}>
                      {(item.patient_first_name || '?')[0]}{(item.patient_last_name || '?')[0]}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={styles.patientName}>{item.patient_name}</span>
                        <span style={{
                          ...styles.sourceBadge,
                          background: (SOURCE_CONFIG[item.source] || SOURCE_CONFIG.sms).bg,
                          color: (SOURCE_CONFIG[item.source] || SOURCE_CONFIG.sms).text
                        }}>
                          {(SOURCE_CONFIG[item.source] || SOURCE_CONFIG.sms).label}
                        </span>
                        {assignedToMe && (
                          <span style={styles.ownedBadge}>Assigned to you</span>
                        )}
                        {assignedToOther && (
                          <span style={styles.otherUserBadge}>
                            Handled by {assigneeName}
                          </span>
                        )}
                        {item.auto_released && (
                          <span style={styles.autoReleasedBadge}>
                            Auto-released{item.previous_assigned_name ? ` from ${item.previous_assigned_name}` : ''}
                          </span>
                        )}
                      </div>
                      <div style={styles.messagePreview}>
                        {item.last_message_preview || 'No message preview'}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={styles.queueItemRight}>
                  <span style={styles.reasonBadge}>
                    {ESCALATION_LABELS[item.escalation_reason] || item.escalation_reason}
                  </span>
                  {item.created_at && (
                    <span style={styles.waitTime}>
                      {(() => {
                        const utc = item.created_at.endsWith('Z') ? item.created_at : item.created_at + 'Z';
                        const mins = Math.floor((Date.now() - new Date(utc).getTime()) / 60000);
                        if (mins < 60) return `${mins}m ago`;
                        if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
                        return new Date(utc).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                      })()}
                    </span>
                  )}

                  {/* Pending items — Take Over button */}
                  {item.status === 'pending' && (
                    <button
                      onClick={() => handleTakeover(item)}
                      style={styles.takeoverButton}
                    >
                      {item.source === 'chat_widget' ? 'Open Chat' : item.source === 'email' ? (openEmailId === item.id ? 'Hide Email' : 'Review Email') : 'Take Over'}
                    </button>
                  )}

                  {/* In-progress items assigned to OTHER user — Reassign to me */}
                  {item.status === 'in_progress' && assignedToOther && (
                    <>
                      {confirmReassign === item.id ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>Take from {assigneeName}?</span>
                          <button onClick={() => handleReassign(item)} style={{ ...styles.takeoverButton, background: '#f59e0b', padding: '4px 10px', fontSize: '12px' }}>
                            Confirm
                          </button>
                          <button onClick={() => setConfirmReassign(null)} style={{ ...styles.filterButton, padding: '4px 8px', fontSize: '12px' }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmReassign(item.id)}
                          style={{ ...styles.takeoverButton, background: '#f59e0b' }}
                        >
                          Reassign to Me
                        </button>
                      )}
                    </>
                  )}

                  {/* In-progress items assigned to ME — Release button */}
                  {item.status === 'in_progress' && assignedToMe && (
                    <button
                      onClick={() => handleUnassign(item)}
                      style={{ ...styles.filterButton, fontSize: '12px', padding: '4px 10px', color: '#6b7280' }}
                    >
                      Release
                    </button>
                  )}

                  {/* Chat view button for non-pending chat items */}
                  {item.source === 'chat_widget' && item.chat_session_id && item.status !== 'pending' && (
                    <button
                      onClick={() => setOpenChatSessionId(
                        openChatSessionId === item.chat_session_id ? null : item.chat_session_id
                      )}
                      style={{ ...styles.filterButton, fontSize: '12px', padding: '4px 10px' }}
                    >
                      {openChatSessionId === item.chat_session_id ? 'Hide Chat' : 'View Chat'}
                    </button>
                  )}
                </div>
              </div>
              {openChatSessionId === item.chat_session_id && item.chat_session_id && (
                <ChatStaffPanel
                  practiceId={practiceId}
                  sessionId={item.chat_session_id}
                  onClose={() => { setOpenChatSessionId(null); loadQueue(); }}
                />
              )}
              {openEmailId === item.id && item.source === 'email' && (
                <div style={styles.emailPanel}>
                  <div style={styles.emailHeader}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '14px' }}>
                        Email Thread — {item.email_from || item.patient_name}
                      </strong>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {item.escalation_reason && (
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                            background: item.escalation_reason === 'VERIFIED_PATIENT' ? '#d1fae5' :
                              item.escalation_reason === 'PATIENT_NOT_FOUND' ? '#fef3c7' :
                              item.escalation_reason === 'UNVERIFIED_SENDER' ? '#fee2e2' : '#f3f4f6',
                            color: item.escalation_reason === 'VERIFIED_PATIENT' ? '#065f46' :
                              item.escalation_reason === 'PATIENT_NOT_FOUND' ? '#92400e' :
                              item.escalation_reason === 'UNVERIFIED_SENDER' ? '#991b1b' : '#374151'
                          }}>
                            {ESCALATION_LABELS[item.escalation_reason] || item.escalation_reason}
                          </span>
                        )}
                        <button onClick={() => { setOpenEmailId(null); setEmailThread([]); }} style={styles.emailCloseBtn}>&times;</button>
                      </div>
                    </div>
                    {item.email_subject && (
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                        Subject: {item.email_subject}
                      </div>
                    )}
                  </div>
                  <div style={styles.threadContainer}>
                    {emailThread.length > 0 ? emailThread.map((msg, idx) => {
                      const isOutbound = msg.direction === 'outbound';
                      const senderLabel = isOutbound
                        ? (msg.sent_by_user_id ? `${msg.sender_first_name || ''} ${msg.sender_last_name || ''}`.trim() || 'Staff' : 'System')
                        : (msg.patient_first_name ? `${msg.patient_first_name} ${msg.patient_last_name}` : msg.from_email);
                      const ts = msg.created_at ? new Date(msg.created_at.endsWith?.('Z') ? msg.created_at : msg.created_at + 'Z').toLocaleString() : '';
                      return (
                        <div key={msg.id || idx} style={{
                          display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                          marginBottom: '10px'
                        }}>
                          <div style={{
                            maxWidth: '80%', padding: '10px 14px', borderRadius: '12px',
                            background: isOutbound ? (msg.sent_by_user_id ? '#dbeafe' : '#e0e7ff') : '#f3f4f6',
                            border: isOutbound ? '1px solid #bfdbfe' : '1px solid #e5e7eb'
                          }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: isOutbound ? '#1d4ed8' : '#6b7280', marginBottom: '4px' }}>
                              {senderLabel}
                              {!msg.sent_by_user_id && isOutbound && (
                                <span style={{ fontWeight: 400, marginLeft: '6px', color: '#9ca3af' }}>Auto</span>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                              {getEmailBodyText(msg)}
                            </div>
                            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px', textAlign: isOutbound ? 'right' : 'left' }}>
                              {ts}
                            </div>
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ padding: '12px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>
                        {item.email_body_text || item.last_message_preview || 'No email content available'}
                      </div>
                    )}
                  </div>
                  {/* Reply section — only show for pending OR if assigned to current user */}
                  {(item.status === 'pending' || assignedToMe) && item.status !== 'resolved' && (
                    <div style={styles.emailReplySection}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Reply to {item.email_from}</div>
                      <textarea
                        value={emailReplyText}
                        onChange={(e) => setEmailReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        rows={4}
                        style={styles.emailReplyInput}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleDraftReply(item)}
                          disabled={draftingReply}
                          style={{
                            ...styles.resolveButton,
                            background: draftingReply ? '#9ca3af' : '#7c3aed',
                            cursor: draftingReply ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {draftingReply ? 'Drafting...' : 'AI Draft'}
                        </button>
                        <button onClick={() => handleResolveEmail(item)} style={styles.resolveButton}>
                          Mark Resolved (No Reply)
                        </button>
                        <button
                          onClick={() => handleSendEmailReply(item)}
                          disabled={sendingReply || !emailReplyText.trim()}
                          style={{
                            ...styles.resolveButton,
                            background: sendingReply || !emailReplyText.trim() ? '#9ca3af' : '#2563eb',
                            cursor: sendingReply || !emailReplyText.trim() ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {sendingReply ? 'Sending...' : 'Send Reply & Resolve'}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Read-only notice for items assigned to other users */}
                  {assignedToOther && item.status !== 'resolved' && (
                    <div style={styles.readOnlyNotice}>
                      This email is being handled by {assigneeName}. Reassign to yourself to reply.
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// =====================================================
// Callbacks Tab (voice, sms, email, chat callbacks)
// =====================================================

function CallbacksTab({ practiceId }) {
  const [callbacks, setCallbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');

  const loadCallbacks = useCallback(async () => {
    if (!practiceId) return;
    try {
      const result = await api.getCallbackTasks(practiceId, {
        status: filter === 'all' ? undefined : filter,
        urgency: urgencyFilter || undefined,
        limit: 100
      });
      if (result.success) {
        setCallbacks(result.data || []);
        setStats(result.stats || null);
      }
    } catch (err) {
      setError('Failed to load callbacks');
      console.error('Load callbacks error:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filter, urgencyFilter]);

  useEffect(() => {
    loadCallbacks();
    const interval = setInterval(loadCallbacks, 15000);
    return () => clearInterval(interval);
  }, [loadCallbacks]);

  const clearMessages = () => { setError(null); setSuccess(null); };

  const handleAssign = async (id) => {
    clearMessages();
    try {
      await api.assignCallbackTask(practiceId, id);
      setSuccess('Callback assigned to you');
      await loadCallbacks();
    } catch (err) {
      setError(err.message || 'Failed to assign callback');
    }
  };

  const handleComplete = async (id) => {
    clearMessages();
    try {
      await api.completeCallbackTask(practiceId, id, completeNotes || null);
      setSuccess('Callback marked as completed');
      setExpandedId(null);
      setCompleteNotes('');
      await loadCallbacks();
    } catch (err) {
      setError(err.message || 'Failed to complete callback');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this callback task?')) return;
    clearMessages();
    try {
      await api.cancelCallbackTask(practiceId, id);
      setSuccess('Callback cancelled');
      await loadCallbacks();
    } catch (err) {
      setError(err.message || 'Failed to cancel callback');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const utc = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const d = new Date(utc);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString();
  };

  const filteredCallbacks = sourceFilter === 'all'
    ? callbacks
    : callbacks.filter(cb => (cb.source || 'voice_ai') === sourceFilter);

  const pendingCount = stats?.total_pending || 0;
  const inProgressCount = stats?.by_status?.in_progress || 0;
  const completedToday = stats?.completed_today || 0;
  const highUrgency = stats?.pending_by_urgency?.high || 0;

  if (loading) return <div style={styles.loading}>Loading callbacks...</div>;

  return (
    <div>
      {error && <div style={styles.errorBanner}>{error}</div>}
      {success && <div style={{ ...styles.errorBanner, background: '#d1fae5', color: '#059669' }}>{success}</div>}

      <div style={styles.summaryRow}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f59e0b' }}>
          <div style={styles.summaryValue}>{pendingCount}</div>
          <div style={styles.summaryLabel}>Pending</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #dc2626' }}>
          <div style={styles.summaryValue}>{highUrgency}</div>
          <div style={styles.summaryLabel}>High Urgency</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3b82f6' }}>
          <div style={styles.summaryValue}>{inProgressCount}</div>
          <div style={styles.summaryLabel}>In Progress</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #10b981' }}>
          <div style={styles.summaryValue}>{completedToday}</div>
          <div style={styles.summaryLabel}>Completed Today</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterRow}>
        {['pending', 'in_progress', 'completed', 'cancelled', 'all'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              ...styles.filterButton,
              ...(filter === status ? styles.filterButtonActive : {})
            }}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
        <span style={{ borderLeft: '1px solid #e5e7eb', margin: '0 4px' }} />
        {['all', 'voice_ai', 'sms', 'chat_widget', 'manual'].map(src => (
          <button
            key={src}
            onClick={() => setSourceFilter(src)}
            style={{
              ...styles.filterButton,
              ...(sourceFilter === src ? styles.filterButtonActive : {})
            }}
          >
            {src === 'all' ? 'All Sources' : (SOURCE_CONFIG[src]?.label || src)}
          </button>
        ))}
        <span style={{ borderLeft: '1px solid #e5e7eb', margin: '0 4px' }} />
        <select
          value={urgencyFilter}
          onChange={e => setUrgencyFilter(e.target.value)}
          style={{ ...styles.filterButton, appearance: 'auto', paddingRight: 24 }}
        >
          <option value="">Any Urgency</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Callback List */}
      <div style={styles.queueList}>
        {filteredCallbacks.length === 0 ? (
          <div style={styles.emptyState}>
            {filter === 'pending'
              ? 'No pending callbacks right now.'
              : `No ${filter.replace('_', ' ')} callbacks.`}
          </div>
        ) : (
          filteredCallbacks.map(cb => {
            const srcConfig = SOURCE_CONFIG[cb.source] || SOURCE_CONFIG.manual;
            const urgencyConfig = URGENCY_COLORS[cb.urgency] || URGENCY_COLORS.medium;
            const patientName = cb.patient_first_name
              ? `${cb.patient_first_name} ${cb.patient_last_name || ''}`.trim()
              : 'Unknown Caller';
            const isExpanded = expandedId === cb.id;

            return (
              <div key={cb.id} style={styles.queueItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={styles.avatar}>
                      {(cb.patient_first_name || '?')[0]}{(cb.patient_last_name || '?')[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={styles.patientName}>{patientName}</span>
                        <span style={{
                          ...styles.sourceBadge,
                          background: srcConfig.bg,
                          color: srcConfig.text
                        }}>
                          {srcConfig.label}
                        </span>
                        <span style={{
                          ...styles.sourceBadge,
                          background: urgencyConfig.bg,
                          color: urgencyConfig.text
                        }}>
                          {urgencyConfig.label}
                        </span>
                        {cb.status === 'completed' && (
                          <span style={{ ...styles.sourceBadge, background: '#d1fae5', color: '#065f46' }}>Completed</span>
                        )}
                        {cb.status === 'cancelled' && (
                          <span style={{ ...styles.sourceBadge, background: '#fee2e2', color: '#991b1b' }}>Cancelled</span>
                        )}
                        {cb.status === 'in_progress' && (
                          <span style={{ ...styles.sourceBadge, background: '#dbeafe', color: '#1e40af' }}>In Progress</span>
                        )}
                      </div>
                      {cb.patient_phone && (
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{cb.patient_phone}</div>
                      )}
                    </div>
                    <span style={styles.waitTime}>{formatTime(cb.created_at)}</span>
                  </div>

                  {/* Reason */}
                  {cb.reason && (
                    <div style={{ fontSize: 13, color: '#374151', marginLeft: 46, marginBottom: 4 }}>
                      {cb.reason}
                    </div>
                  )}

                  {/* Notes */}
                  {cb.notes && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 46, fontStyle: 'italic' }}>
                      {cb.notes}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, marginLeft: 46, marginTop: 8 }}>
                    {cb.status === 'pending' && (
                      <>
                        <button onClick={() => handleAssign(cb.id)} style={styles.takeoverButton}>
                          Assign to Me
                        </button>
                        <button
                          onClick={() => handleCancel(cb.id)}
                          style={{ ...styles.filterButton, fontSize: 12, padding: '4px 10px', color: '#dc2626', borderColor: '#fecaca' }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {cb.status === 'in_progress' && (
                      <>
                        <button
                          onClick={() => { setExpandedId(isExpanded ? null : cb.id); setCompleteNotes(''); }}
                          style={{ ...styles.takeoverButton, background: '#10b981' }}
                        >
                          {isExpanded ? 'Hide' : 'Complete'}
                        </button>
                        <button
                          onClick={() => handleCancel(cb.id)}
                          style={{ ...styles.filterButton, fontSize: 12, padding: '4px 10px', color: '#dc2626', borderColor: '#fecaca' }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>

                  {/* Expanded complete form */}
                  {isExpanded && (
                    <div style={{ marginLeft: 46, marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        style={{
                          flex: 1, padding: '6px 10px', border: '1px solid #d1d5db',
                          borderRadius: 6, fontSize: 13
                        }}
                        placeholder="Resolution notes (optional)..."
                        value={completeNotes}
                        onChange={e => setCompleteNotes(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleComplete(cb.id)}
                      />
                      <button
                        onClick={() => handleComplete(cb.id)}
                        style={{ ...styles.takeoverButton, background: '#10b981' }}
                      >
                        Confirm
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// =====================================================
// Main AiQueue Component — unified hub with tabs
// =====================================================

function AiQueue({ practiceId }) {
  const { user } = useAuth();
  const currentUserId = user?.user?.id || user?.activeContext?.userId;

  // Single-page view: both sections stacked with type badges. Conversations and
  // callbacks have different shapes (channels vs urgency, claim vs complete) so
  // they keep independent filters/state, but staff no longer has to switch tabs
  // to see what's outstanding.
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>AI Review Hub</h2>
        <p style={styles.subtitle}>All items needing human attention — conversations, callbacks, and escalations</p>
      </div>

      <div style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Conversations</span>
        <span style={{ ...styles.typeBadge, background: '#dbeafe', color: '#1e40af' }}>Conversation</span>
      </div>
      <ConversationsTab practiceId={practiceId} currentUserId={currentUserId} />

      <div style={{ ...styles.sectionHeader, marginTop: 32 }}>
        <span style={styles.sectionTitle}>Callbacks</span>
        <span style={{ ...styles.typeBadge, background: '#fef3c7', color: '#92400e' }}>Callback</span>
      </div>
      <CallbacksTab practiceId={practiceId} />
    </div>
  );
}

// =====================================================
// Styles
// =====================================================

const styles = {
  container: {
    padding: '24px'
  },
  header: {
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px'
  },
  // Section header used now that the tabs are gone — visually anchors each
  // sub-list and shows the type badge inline.
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
  },
  typeBadge: {
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280'
  },
  errorBanner: {
    padding: '12px 16px',
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  summaryRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px'
  },
  summaryCard: {
    flex: 1,
    background: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827'
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px'
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  filterButton: {
    padding: '8px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    background: '#fff',
    color: '#374151',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  filterButtonActive: {
    background: '#3b82f6',
    color: '#fff',
    borderColor: '#3b82f6'
  },
  queueList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#9ca3af',
    fontSize: '14px'
  },
  queueItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    background: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.15s ease'
  },
  queueItemLeft: {
    flex: 1,
    minWidth: 0
  },
  queueItemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0
  },
  patientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#e0e7ff',
    color: '#4f46e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    flexShrink: 0
  },
  patientName: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#111827'
  },
  messagePreview: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '400px'
  },
  reasonBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    background: '#fef3c7',
    color: '#92400e'
  },
  waitTime: {
    fontSize: '12px',
    color: '#9ca3af'
  },
  takeoverButton: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  assignedTo: {
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic'
  },
  sourceBadge: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: '600'
  },
  sourceBadgeSms: {
    background: '#dbeafe',
    color: '#1e40af'
  },
  sourceBadgeChat: {
    background: '#d1fae5',
    color: '#065f46'
  },
  emailPanel: {
    margin: '0 16px 12px', padding: '16px', background: '#fff',
    border: '1px solid #e5e7eb', borderRadius: '8px'
  },
  emailHeader: {
    paddingBottom: '12px', borderBottom: '1px solid #f3f4f6', marginBottom: '12px'
  },
  emailCloseBtn: {
    background: 'none', border: 'none', fontSize: '20px', color: '#9ca3af',
    cursor: 'pointer', padding: '0 4px', lineHeight: 1
  },
  threadContainer: {
    maxHeight: '400px', overflowY: 'auto', padding: '8px 0'
  },
  emailBody: {
    fontSize: '13px', color: '#374151', lineHeight: '1.6',
    whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto'
  },
  emailActions: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f3f4f6'
  },
  resolveButton: {
    padding: '6px 16px', background: '#10b981', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
  },
  emailReplySection: {
    marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb'
  },
  emailReplyInput: {
    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px',
    fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.5',
    outline: 'none', boxSizing: 'border-box'
  },
  ownedBadge: {
    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    background: '#dbeafe', color: '#1e40af'
  },
  otherUserBadge: {
    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    background: '#fef3c7', color: '#92400e'
  },
  autoReleasedBadge: {
    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500,
    background: '#f3f4f6', color: '#6b7280', fontStyle: 'italic'
  },
  readOnlyNotice: {
    marginTop: '12px', padding: '10px 14px', background: '#fef3c7', color: '#92400e',
    borderRadius: '6px', fontSize: '13px', textAlign: 'center'
  }
};

export default AiQueue;
